// src/pages/SuperAdmin/GlobalCommunicationManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Badge,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
} from 'reactstrap';
import Select from 'react-select';
import { get, post, put, del } from 'helpers/api_helper';
import { showSuccessAlert, showErroeAlert } from 'pages/utils/alertMessages';
import moment from 'moment';
import {
  RiMailLine,
  RiMailSendLine,
  RiWhatsappLine,
  RiSendPlaneLine,
  RiSettings4Line,
  RiShieldCheckLine,
  RiHistoryLine,
  RiRefreshLine,
  RiKeyLine,
  RiServerLine,
  RiGlobalLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiAlertLine,
  RiTimeLine,
  RiUserHeartLine,
  RiBuildingLine,
  RiEyeLine,
  RiEditLine,
  RiDeleteBinLine,
  RiAddLine,
  RiFileTextLine,
  RiExternalLinkLine,
  RiPriceTag3Line,
  RiCodeLine,
  RiBankCardLine,
  RiSecurePaymentLine,
  RiCheckboxCircleFill,
} from 'react-icons/ri';

const GlobalCommunicationManager = () => {
  const [activeSubTab, setActiveSubTab] = useState('meta_whatsapp');
  const [loading, setLoading] = useState(true);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingWa, setSavingWa] = useState(false);
  const [savingPayu, setSavingPayu] = useState(false);
  const [sendingEmailTest, setSendingEmailTest] = useState(false);
  const [sendingWaTest, setSendingWaTest] = useState(false);

  // Global Config Form State
  const [globalConfig, setGlobalConfig] = useState({
    smtp_host: 'smtp.zoho.in',
    smtp_port: 465,
    smtp_encryption: 'ssl',
    smtp_username: 'support@oralstop.com',
    smtp_password: '',
    has_smtp_password: true,
    smtp_from_email: 'support@oralstop.com',
    smtp_from_name: 'Oralstop Dental Cloud',
    smtp_is_active: true,

    whatsapp_api_provider: 'META_CLOUD_API',
    whatsapp_phone_number_id: '',
    whatsapp_waba_id: '',
    whatsapp_access_token: '',
    has_whatsapp_access_token: false,
    whatsapp_api_version: 'v20.0',
    whatsapp_gateway_url: 'https://graph.facebook.com/v20.0',
    whatsapp_session_id: 'global_session_oralstop',
    whatsapp_phone_number: '+91-9090909090',
    whatsapp_is_active: true,

    payu_merchant_key: '',
    payu_merchant_salt: '',
    has_payu_salt: false,
    payu_auth_header: '',
    payu_env: 'test',
    payu_is_active: true,
  });

  // Test Dispatch State
  const [testEmailTarget, setTestEmailTarget] = useState('admin@oralstop.com');
  const [testWaTarget, setTestWaTarget] = useState('919876543210');

  // Templates Management State
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateFilter, setTemplateFilter] = useState('ALL');
  const [isTemplateModal, setIsTemplateModal] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const [templateForm, setTemplateForm] = useState({
    template_type: 'WHATSAPP',
    template_key: 'APPOINTMENT_CONFIRMATION',
    title: '',
    category: 'UTILITY',
    language_code: 'en_US',
    meta_template_name: '',
    meta_status: 'APPROVED',
    subject: '',
    body_text: '',
    html_content: '',
    header_type: 'TEXT',
    header_text: '',
    footer_text: '',
    is_active: true,
  });

  // Logs State
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState({
    channel: 'ALL',
    status: 'ALL',
    search: '',
  });
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchGlobalConfig = async () => {
    setLoading(true);
    try {
      const res = await get('/superadmin/global-communication');
      if (res?.success && res?.body) {
        setGlobalConfig((prev) => ({
          ...prev,
          ...res.body,
        }));
      }
    } catch (err) {
      console.warn('Notice loading global communication config:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await get('/superadmin/communication-templates');
      if (res?.success && res?.body) {
        setTemplates(res.body);
      }
    } catch (err) {
      console.warn('Error fetching global templates:', err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams();
      if (logFilter.channel !== 'ALL') params.append('channel', logFilter.channel);
      if (logFilter.status !== 'ALL') params.append('status', logFilter.status);
      if (logFilter.search) params.append('search', logFilter.search);

      const res = await get(`/superadmin/communication-logs?${params.toString()}`);
      if (res?.success && res?.body?.items) {
        setLogs(res.body.items);
      } else if (Array.isArray(res?.body)) {
        setLogs(res.body);
      }
    } catch (err) {
      console.warn('Error fetching logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchGlobalConfig();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (activeSubTab === 'templates') {
      fetchTemplates();
    } else if (activeSubTab === 'logs') {
      fetchLogs();
    }
  }, [activeSubTab, logFilter]);

  // Save SMTP Settings
  const handleSaveSmtp = async (e) => {
    if (e) e.preventDefault();
    setSavingSmtp(true);
    try {
      const payload = {
        smtp_host: globalConfig.smtp_host,
        smtp_port: globalConfig.smtp_port,
        smtp_encryption: globalConfig.smtp_encryption,
        smtp_username: globalConfig.smtp_username,
        smtp_from_email: globalConfig.smtp_from_email,
        smtp_from_name: globalConfig.smtp_from_name,
        smtp_is_active: globalConfig.smtp_is_active,
      };
      if (globalConfig.smtp_password && !globalConfig.smtp_password.includes('•••')) {
        payload.smtp_password = globalConfig.smtp_password;
      }

      const res = await post('/superadmin/global-communication', payload);
      if (res?.success) {
        showSuccessAlert('Global SMTP configuration saved successfully!');
        fetchGlobalConfig();
      } else {
        showErroeAlert(res?.message || 'Failed to save Global SMTP config.');
      }
    } catch (err) {
      showErroeAlert('Error saving SMTP: ' + err.message);
    } finally {
      setSavingSmtp(false);
    }
  };

  // Save WhatsApp Meta Cloud API Settings
  const handleSaveWhatsApp = async (e) => {
    if (e) e.preventDefault();
    setSavingWa(true);
    try {
      const payload = {
        whatsapp_api_provider: globalConfig.whatsapp_api_provider,
        whatsapp_phone_number_id: globalConfig.whatsapp_phone_number_id,
        whatsapp_waba_id: globalConfig.whatsapp_waba_id,
        whatsapp_api_version: globalConfig.whatsapp_api_version,
        whatsapp_gateway_url: globalConfig.whatsapp_gateway_url,
        whatsapp_phone_number: globalConfig.whatsapp_phone_number,
        whatsapp_is_active: globalConfig.whatsapp_is_active,
      };
      if (globalConfig.whatsapp_access_token && !globalConfig.whatsapp_access_token.includes('•••')) {
        payload.whatsapp_access_token = globalConfig.whatsapp_access_token;
      }

      const res = await post('/superadmin/global-communication', payload);
      if (res?.success) {
        showSuccessAlert('Meta WhatsApp Cloud API configuration saved successfully!');
        fetchGlobalConfig();
      } else {
        showErroeAlert(res?.message || 'Failed to save WhatsApp config.');
      }
    } catch (err) {
      showErroeAlert('Error saving WhatsApp settings: ' + err.message);
    } finally {
      setSavingWa(false);
    }
  };

  // Save PayU India Gateway Settings
  const handleSavePayu = async (e) => {
    if (e) e.preventDefault();
    setSavingPayu(true);
    try {
      const payload = {
        payu_merchant_key: globalConfig.payu_merchant_key,
        payu_auth_header: globalConfig.payu_auth_header,
        payu_env: globalConfig.payu_env,
        payu_is_active: globalConfig.payu_is_active,
      };
      if (globalConfig.payu_merchant_salt && !globalConfig.payu_merchant_salt.includes('•••')) {
        payload.payu_merchant_salt = globalConfig.payu_merchant_salt;
      }

      const res = await post('/superadmin/global-communication', payload);
      if (res?.success) {
        showSuccessAlert('PayU India Subscription Gateway credentials saved successfully!');
        fetchGlobalConfig();
      } else {
        showErroeAlert(res?.message || 'Failed to save PayU config.');
      }
    } catch (err) {
      showErroeAlert('Error saving PayU settings: ' + err.message);
    } finally {
      setSavingPayu(false);
    }
  };

  // Test Global Email
  const handleTestEmail = async () => {
    if (!testEmailTarget) {
      showErroeAlert('Please enter a recipient email address.');
      return;
    }
    setSendingEmailTest(true);
    try {
      const res = await post('/superadmin/global-communication/test-email', {
        to_email: testEmailTarget,
      });
      if (res?.success) {
        showSuccessAlert(`Test email successfully sent to ${testEmailTarget}!`);
      } else {
        showErroeAlert(res?.message || 'Failed to send test email.');
      }
    } catch (err) {
      showErroeAlert('Test email dispatch error: ' + err.message);
    } finally {
      setSendingEmailTest(false);
    }
  };

  // Test Global WhatsApp
  const handleTestWhatsApp = async () => {
    if (!testWaTarget) {
      showErroeAlert('Please enter a recipient WhatsApp number.');
      return;
    }
    setSendingWaTest(true);
    try {
      const res = await post('/superadmin/global-communication/test-whatsapp', {
        to_mobile: testWaTarget,
      });
      if (res?.success) {
        showSuccessAlert(`Test WhatsApp message sent to ${testWaTarget}!`);
      } else {
        showErroeAlert(res?.message || 'Failed to send test WhatsApp message.');
      }
    } catch (err) {
      showErroeAlert('Test WhatsApp dispatch error: ' + err.message);
    } finally {
      setSendingWaTest(false);
    }
  };

  // Open Template Form Modal
  const openTemplateModal = (tpl = null) => {
    if (tpl) {
      setEditingTemplate(tpl);
      setTemplateForm({
        template_type: tpl.template_type,
        template_key: tpl.template_key,
        title: tpl.title,
        category: tpl.category || 'UTILITY',
        language_code: tpl.language_code || 'en_US',
        meta_template_name: tpl.meta_template_name || '',
        meta_status: tpl.meta_status || 'APPROVED',
        subject: tpl.subject || '',
        body_text: tpl.body_text || '',
        html_content: tpl.html_content || '',
        header_type: tpl.header_type || 'TEXT',
        header_text: tpl.header_text || '',
        footer_text: tpl.footer_text || '',
        is_active: tpl.is_active,
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        template_type: 'WHATSAPP',
        template_key: 'CUSTOM_' + Date.now().toString().slice(-4),
        title: '',
        category: 'UTILITY',
        language_code: 'en_US',
        meta_template_name: '',
        meta_status: 'APPROVED',
        subject: '',
        body_text: 'Dear {{patient_name}}, this is a notice from {{clinic_name}}.',
        html_content: '',
        header_type: 'TEXT',
        header_text: 'Notice',
        footer_text: 'Oralstop Dental Healthcare',
        is_active: true,
      });
    }
    setIsTemplateModal(true);
  };

  // Save Template
  const handleSaveTemplate = async (e) => {
    if (e) e.preventDefault();
    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        const res = await put(`/superadmin/communication-templates/${editingTemplate.id}`, templateForm);
        if (res?.success) {
          showSuccessAlert('Template updated successfully!');
          setIsTemplateModal(false);
          fetchTemplates();
        } else {
          showErroeAlert(res?.message || 'Failed to update template.');
        }
      } else {
        const res = await post('/superadmin/communication-templates', templateForm);
        if (res?.success) {
          showSuccessAlert('New Global Template created successfully!');
          setIsTemplateModal(false);
          fetchTemplates();
        } else {
          showErroeAlert(res?.message || 'Failed to create template.');
        }
      }
    } catch (err) {
      showErroeAlert('Error saving template: ' + err.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  // Delete Template
  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this global template?')) return;
    try {
      const res = await del(`/superadmin/communication-templates/${id}`);
      if (res?.success) {
        showSuccessAlert('Template deleted successfully!');
        fetchTemplates();
      }
    } catch (err) {
      showErroeAlert('Error deleting template: ' + err.message);
    }
  };

  // Seed Standard Master Templates
  const handleSeedTemplates = async () => {
    if (!window.confirm('Seed pre-configured Meta WhatsApp & Email standard clinical templates?')) return;
    try {
      const res = await post('/superadmin/seed-communication-templates', {});
      if (res?.success) {
        showSuccessAlert('Standard Global Templates seeded successfully!');
        fetchTemplates();
      }
    } catch (err) {
      showErroeAlert('Error seeding templates: ' + err.message);
    }
  };

  // Insert Dynamic Tag Helper
  const insertTag = (tag) => {
    setTemplateForm((prev) => ({
      ...prev,
      body_text: (prev.body_text || '') + ` ${tag} `,
    }));
  };

  // Render Sample Preview
  const getRenderedPreview = (tpl) => {
    if (!tpl) return '';
    const sampleData = {
      patient_name: 'John Doe',
      doctor_name: 'Dr. Sarah Wilson',
      appointment_date: 'Tomorrow, 10:30 AM',
      clinic_name: 'Apex Dental Care',
      chair_code: 'Chair #2',
      number: '+91 9876543210',
      amount: '₹ 1,500',
    };
    let text = tpl.body_text || '';
    Object.keys(sampleData).forEach((key) => {
      text = text.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'gi'), sampleData[key]);
    });
    return text;
  };

  const renderStatusBadge = (status) => {
    switch (status) {
      case 'SENT':
        return (
          <span className="badge bg-soft-success text-success border border-success font-size-11">
            <RiCheckboxCircleLine className="me-1" /> Sent
          </span>
        );
      case 'FAILED':
        return (
          <span className="badge bg-soft-danger text-danger border border-danger font-size-11">
            <RiCloseCircleLine className="me-1" /> Failed
          </span>
        );
      case 'NOTIFICATION_DISABLED':
        return (
          <span className="badge bg-soft-warning text-warning border border-warning font-size-11">
            <RiAlertLine className="me-1" /> Toggle Disabled
          </span>
        );
      case 'CREDENTIAL_NOT_CONFIGURED':
        return (
          <span className="badge bg-soft-secondary text-secondary border font-size-11">
            <RiKeyLine className="me-1" /> No Credentials
          </span>
        );
      case 'SKIPPED':
      default:
        return (
          <span className="badge bg-light text-muted border font-size-11">
            <RiTimeLine className="me-1" /> Skipped
          </span>
        );
    }
  };

  const renderSourceBadge = (source) => {
    if (source === 'TENANT') {
      return (
        <span className="badge bg-soft-info text-info border border-info font-size-11">
          <RiBuildingLine className="me-1" /> Tenant
        </span>
      );
    }
    if (source === 'GLOBAL') {
      return (
        <span className="badge bg-soft-primary text-primary border border-primary font-size-11">
          <RiGlobalLine className="me-1" /> Global
        </span>
      );
    }
    return <span className="badge bg-light text-muted font-size-11">None</span>;
  };

  const filteredTemplates = templates.filter((tpl) => {
    if (templateFilter === 'ALL') return true;
    return tpl.template_type === templateFilter;
  });

  if (loading) {
    return (
      <div className="py-5 text-center">
        <Spinner color="primary" size="md" />
        <p className="mt-2 text-muted font-size-13">Loading Global Communication & Meta Cloud API settings...</p>
      </div>
    );
  }

  return (
    <div className="global-communication-manager">
      {/* Priority Policy Banner */}
      <Card className="border-0 shadow-sm mb-4 bg-soft-primary border-start border-4 border-primary">
        <CardBody className="py-3 px-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-circle p-2 bg-primary text-white font-size-18 d-flex align-items-center justify-content-center"
                style={{ width: 42, height: 42 }}
              >
                <RiGlobalLine />
              </div>
              <div>
                <h6 className="font-weight-bold text-dark mb-0 d-flex align-items-center gap-2">
                  Meta WhatsApp Business Platform & Global SMTP Command Center
                </h6>
                <p className="text-muted font-size-12 mb-0">
                  Manage official Meta Cloud API credentials, global templates, and SMTP outgoing infrastructure.
                  Priority: <strong className="text-dark">Tenant Custom Credentials</strong> →{' '}
                  <strong className="text-primary">SuperAdmin Global Credentials</strong> →{' '}
                  <strong className="text-danger">Skip & Log</strong>.
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <a
                href="https://developers.facebook.com/documentation/business-messaging/whatsapp/overview"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary font-size-12 d-inline-flex align-items-center gap-1"
              >
                <RiExternalLinkLine /> Meta WhatsApp API Docs
              </a>
              <Badge color="primary" className="px-3 py-2 font-size-12">
                <RiShieldCheckLine className="me-1" /> SuperAdmin Exclusive
              </Badge>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Navigation Sub-Tabs */}
      <Nav tabs className="nav-tabs-custom mb-3">
        <NavItem>
          <NavLink
            className={activeSubTab === 'meta_whatsapp' ? 'active' : ''}
            onClick={() => setActiveSubTab('meta_whatsapp')}
            style={{ cursor: 'pointer' }}
          >
            <RiWhatsappLine className="me-1 font-size-16 text-success" />
            <span>Meta WhatsApp Cloud API</span>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeSubTab === 'smtp' ? 'active' : ''}
            onClick={() => setActiveSubTab('smtp')}
            style={{ cursor: 'pointer' }}
          >
            <RiMailLine className="me-1 font-size-16 text-primary" />
            <span>Global SMTP Server</span>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeSubTab === 'templates' ? 'active' : ''}
            onClick={() => setActiveSubTab('templates')}
            style={{ cursor: 'pointer' }}
          >
            <RiFileTextLine className="me-1 font-size-16 text-warning" />
            <span>Global Templates Studio ({templates.length})</span>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeSubTab === 'payu' ? 'active' : ''}
            onClick={() => setActiveSubTab('payu')}
            style={{ cursor: 'pointer' }}
          >
            <RiBankCardLine className="me-1 font-size-16 text-danger" />
            <span>PayU Subscription Gateway</span>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeSubTab === 'logs' ? 'active' : ''}
            onClick={() => setActiveSubTab('logs')}
            style={{ cursor: 'pointer' }}
          >
            <RiHistoryLine className="me-1 font-size-16 text-info" />
            <span>Communication Audit Logs</span>
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeSubTab}>
        {/* ================= META WHATSAPP CLOUD API TAB ================= */}
        <TabPane tabId="meta_whatsapp">
          <Row>
            <Col lg={8}>
              <Card className="border shadow-sm">
                <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-2">
                      <RiWhatsappLine className="text-success font-size-22" />
                      Meta WhatsApp Business Cloud API Configuration
                    </h5>
                    <small className="text-muted">
                      Direct WhatsApp Cloud API credentials via Meta for Developers (Graph API)
                    </small>
                  </div>
                  <div className="form-check form-switch">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="waIsActive"
                      checked={globalConfig.whatsapp_is_active}
                      onChange={(e) => setGlobalConfig((p) => ({ ...p, whatsapp_is_active: e.target.checked }))}
                    />
                    <Label className="form-check-label font-weight-semibold font-size-12" for="waIsActive">
                      {globalConfig.whatsapp_is_active ? 'Active' : 'Disabled'}
                    </Label>
                  </div>
                </CardHeader>
                <CardBody className="p-4">
                  <Form onSubmit={handleSaveWhatsApp}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">
                            Meta Phone Number ID *
                          </Label>
                          <Input
                            type="text"
                            placeholder="e.g. 109283746501928"
                            value={globalConfig.whatsapp_phone_number_id}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, whatsapp_phone_number_id: e.target.value }))}
                            required
                          />
                          <small className="text-muted font-size-11">
                            Found in Meta App Dashboard → WhatsApp → API Setup
                          </small>
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">
                            WhatsApp Business Account ID (WABA ID)
                          </Label>
                          <Input
                            type="text"
                            placeholder="e.g. 98712365401928"
                            value={globalConfig.whatsapp_waba_id}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, whatsapp_waba_id: e.target.value }))}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mt-2">
                      <Col md={12}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">
                            Permanent System User Access Token *{' '}
                            {globalConfig.has_whatsapp_access_token && (
                              <span className="text-success font-size-11 font-weight-normal">(Saved in Vault)</span>
                            )}
                          </Label>
                          <Input
                            type="password"
                            placeholder={
                              globalConfig.has_whatsapp_access_token
                                ? '•••••••• (Leave blank to keep current)'
                                : 'EAAB... (Paste Permanent Meta Access Token)'
                            }
                            value={globalConfig.whatsapp_access_token}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, whatsapp_access_token: e.target.value }))}
                          />
                          <small className="text-muted font-size-11">
                            Token must have <code className="text-primary">whatsapp_business_messaging</code> and <code className="text-primary">whatsapp_business_management</code> permissions.
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mt-2">
                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Graph API Version</Label>
                          <Input
                            type="text"
                            placeholder="v20.0"
                            value={globalConfig.whatsapp_api_version}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, whatsapp_api_version: e.target.value }))}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Registered Sender Phone Number</Label>
                          <Input
                            type="text"
                            placeholder="+91-9090909090"
                            value={globalConfig.whatsapp_phone_number}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, whatsapp_phone_number: e.target.value }))}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2 mt-4 pt-2 border-top">
                      <Button
                        type="button"
                        color="light"
                        onClick={fetchGlobalConfig}
                        disabled={savingWa}
                      >
                        Reset Changes
                      </Button>
                      <Button
                        type="submit"
                        color="success"
                        disabled={savingWa}
                        className="px-4 text-white"
                      >
                        {savingWa ? <Spinner size="sm" className="me-1" /> : <RiShieldCheckLine className="me-1" />}
                        Save Meta Cloud API Config
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            {/* Test WhatsApp Tool */}
            <Col lg={4}>
              <Card className="border shadow-sm bg-light">
                <CardHeader className="bg-white border-bottom py-3">
                  <h6 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-1.5">
                    <RiWhatsappLine className="text-success font-size-18" />
                    Test Meta Cloud API Dispatch
                  </h6>
                  <small className="text-muted">Verify Meta Cloud API deliverability</small>
                </CardHeader>
                <CardBody className="p-3">
                  <FormGroup>
                    <Label className="font-weight-semibold font-size-12">Recipient Mobile Number (with Country Code)</Label>
                    <Input
                      type="text"
                      value={testWaTarget}
                      onChange={(e) => setTestWaTarget(e.target.value)}
                      placeholder="e.g. 919876543210"
                    />
                  </FormGroup>
                  <Button
                    color="success"
                    block
                    onClick={handleTestWhatsApp}
                    disabled={sendingWaTest}
                    className="mt-3 text-white"
                  >
                    {sendingWaTest ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Sending via Meta API...
                      </>
                    ) : (
                      <>
                        <RiSendPlaneLine className="me-1" /> Send Test WhatsApp
                      </>
                    )}
                  </Button>
                  <p className="text-muted font-size-11 mt-3 mb-0">
                    A test message will be dispatched directly to Meta Graph API endpoint <code className="text-dark">/v20.0/{globalConfig.whatsapp_phone_number_id || '{phone_number_id}'}/messages</code>.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* ================= SMTP TAB ================= */}
        <TabPane tabId="smtp">
          <Row>
            <Col lg={8}>
              <Card className="border shadow-sm">
                <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-2">
                      <RiServerLine className="text-primary" />
                      Global Default SMTP Email Configuration
                    </h5>
                    <small className="text-muted">
                      Default outgoing mail server used by all clinics that have not configured their own SMTP credentials.
                    </small>
                  </div>
                  <div className="form-check form-switch">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="smtpIsActive"
                      checked={globalConfig.smtp_is_active}
                      onChange={(e) => setGlobalConfig((p) => ({ ...p, smtp_is_active: e.target.checked }))}
                    />
                    <Label className="form-check-label font-weight-semibold font-size-12" for="smtpIsActive">
                      {globalConfig.smtp_is_active ? 'Active' : 'Disabled'}
                    </Label>
                  </div>
                </CardHeader>
                <CardBody className="p-4">
                  <Form onSubmit={handleSaveSmtp}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">SMTP Server Host *</Label>
                          <Input
                            type="text"
                            placeholder="e.g. smtp.zoho.in or smtp.gmail.com"
                            value={globalConfig.smtp_host}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, smtp_host: e.target.value }))}
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Port *</Label>
                          <Input
                            type="number"
                            placeholder="465 or 587"
                            value={globalConfig.smtp_port}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, smtp_port: e.target.value }))}
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Encryption *</Label>
                          <Select
                            value={{ value: globalConfig.smtp_encryption, label: globalConfig.smtp_encryption?.toUpperCase() }}
                            options={[
                              { value: 'ssl', label: 'SSL' },
                              { value: 'tls', label: 'TLS' },
                            ]}
                            onChange={(val) => setGlobalConfig((p) => ({ ...p, smtp_encryption: val.value }))}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mt-2">
                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">SMTP Username / Account Email *</Label>
                          <Input
                            type="email"
                            placeholder="support@oralstop.com"
                            value={globalConfig.smtp_username}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, smtp_username: e.target.value }))}
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">
                            SMTP Password / App Key * {globalConfig.has_smtp_password && <span className="text-success font-size-11 font-weight-normal">(Saved)</span>}
                          </Label>
                          <Input
                            type="password"
                            placeholder={globalConfig.has_smtp_password ? '•••••••• (Leave blank to keep current)' : 'Enter SMTP password'}
                            value={globalConfig.smtp_password}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, smtp_password: e.target.value }))}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mt-2">
                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Default Sender Email (From Address)</Label>
                          <Input
                            type="email"
                            placeholder="support@oralstop.com"
                            value={globalConfig.smtp_from_email}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, smtp_from_email: e.target.value }))}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Sender Display Name</Label>
                          <Input
                            type="text"
                            placeholder="Oralstop Dental Cloud"
                            value={globalConfig.smtp_from_name}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, smtp_from_name: e.target.value }))}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2 mt-4 pt-2 border-top">
                      <Button
                        type="button"
                        color="light"
                        onClick={fetchGlobalConfig}
                        disabled={savingSmtp}
                      >
                        Reset Changes
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        disabled={savingSmtp}
                        className="px-4"
                      >
                        {savingSmtp ? <Spinner size="sm" className="me-1" /> : <RiShieldCheckLine className="me-1" />}
                        Save Global SMTP
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            {/* Test SMTP Tool */}
            <Col lg={4}>
              <Card className="border shadow-sm bg-light">
                <CardHeader className="bg-white border-bottom py-3">
                  <h6 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-1.5">
                    <RiMailSendLine className="text-primary font-size-16" />
                    Test Global SMTP Dispatch
                  </h6>
                  <small className="text-muted">Verify connectivity and sending ability</small>
                </CardHeader>
                <CardBody className="p-3">
                  <FormGroup>
                    <Label className="font-weight-semibold font-size-12">Recipient Email Address</Label>
                    <Input
                      type="email"
                      value={testEmailTarget}
                      onChange={(e) => setTestEmailTarget(e.target.value)}
                      placeholder="e.g. admin@oralstop.com"
                    />
                  </FormGroup>
                  <Button
                    color="primary"
                    block
                    onClick={handleTestEmail}
                    disabled={sendingEmailTest}
                    className="mt-3"
                  >
                    {sendingEmailTest ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Sending Test Email...
                      </>
                    ) : (
                      <>
                        <RiSendPlaneLine className="me-1" /> Dispatch Test Email
                      </>
                    )}
                  </Button>
                  <p className="text-muted font-size-11 mt-3 mb-0">
                    A test verification email will be sent through the Global SMTP transporter.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* ================= GLOBAL TEMPLATES STUDIO TAB ================= */}
        <TabPane tabId="templates">
          <Card className="border shadow-sm">
            <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h5 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-2">
                  <RiFileTextLine className="text-warning font-size-20" />
                  Global WhatsApp Meta & Email Templates Studio
                </h5>
                <small className="text-muted">
                  Centrally curated templates for WhatsApp Cloud API & Email. Only Super Admin has management permissions.
                </small>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  size="sm"
                  color="outline-secondary"
                  onClick={handleSeedTemplates}
                  title="Seed Pre-Approved Clinical Master Templates"
                >
                  <RiShieldCheckLine className="me-1 text-success" /> Seed Standard Presets
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  onClick={() => openTemplateModal(null)}
                >
                  <RiAddLine className="me-1" /> Create Global Template
                </Button>
              </div>
            </CardHeader>

            <CardBody className="p-3">
              {/* Filter Tabs */}
              <div className="d-flex gap-2 mb-3">
                <Button
                  size="sm"
                  color={templateFilter === 'ALL' ? 'primary' : 'light'}
                  onClick={() => setTemplateFilter('ALL')}
                >
                  All ({templates.length})
                </Button>
                <Button
                  size="sm"
                  color={templateFilter === 'WHATSAPP' ? 'success' : 'light'}
                  onClick={() => setTemplateFilter('WHATSAPP')}
                >
                  <RiWhatsappLine className="me-1" /> WhatsApp Meta ({templates.filter((t) => t.template_type === 'WHATSAPP').length})
                </Button>
                <Button
                  size="sm"
                  color={templateFilter === 'EMAIL' ? 'info' : 'light'}
                  onClick={() => setTemplateFilter('EMAIL')}
                >
                  <RiMailLine className="me-1" /> Email Templates ({templates.filter((t) => t.template_type === 'EMAIL').length})
                </Button>
              </div>

              {/* Templates Table */}
              <div className="table-responsive">
                <Table hover className="align-middle mb-0 font-size-13">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '100px' }}>Channel</th>
                      <th>Template Title & Key</th>
                      <th style={{ width: '180px' }}>Meta Template Name</th>
                      <th style={{ width: '120px' }}>Category</th>
                      <th style={{ width: '100px' }}>Meta Status</th>
                      <th style={{ width: '80px' }}>Active</th>
                      <th style={{ width: '120px' }} className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templatesLoading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          <Spinner size="sm" className="me-2" /> Loading global templates...
                        </td>
                      </tr>
                    ) : filteredTemplates.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          No global templates configured. Click &quot;Seed Standard Presets&quot; to populate.
                        </td>
                      </tr>
                    ) : (
                      filteredTemplates.map((tpl) => (
                        <tr key={tpl.id}>
                          <td>
                            {tpl.template_type === 'WHATSAPP' ? (
                              <Badge color="success" className="font-size-11">
                                <RiWhatsappLine className="me-1" /> WhatsApp
                              </Badge>
                            ) : (
                              <Badge color="primary" className="font-size-11">
                                <RiMailLine className="me-1" /> Email
                              </Badge>
                            )}
                          </td>
                          <td>
                            <div>
                              <strong className="text-dark d-block">{tpl.title}</strong>
                              <code className="text-muted font-size-11">{tpl.template_key}</code>
                            </div>
                          </td>
                          <td>
                            {tpl.meta_template_name ? (
                              <code className="text-primary font-size-12">{tpl.meta_template_name}</code>
                            ) : (
                              <span className="text-muted font-size-11">—</span>
                            )}
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border font-size-11">
                              {tpl.category}
                            </span>
                          </td>
                          <td>
                            {tpl.meta_status === 'APPROVED' ? (
                              <span className="badge bg-soft-success text-success border border-success font-size-11">
                                <RiCheckboxCircleLine className="me-1" /> Approved
                              </span>
                            ) : (
                              <span className="badge bg-soft-warning text-warning border font-size-11">
                                {tpl.meta_status}
                              </span>
                            )}
                          </td>
                          <td>
                            {tpl.is_active ? (
                              <Badge color="success">ON</Badge>
                            ) : (
                              <Badge color="secondary">OFF</Badge>
                            )}
                          </td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-1">
                              <Button
                                size="sm"
                                color="light"
                                onClick={() => setPreviewTemplate(tpl)}
                                title="Live Preview with Clinic Data"
                              >
                                <RiEyeLine />
                              </Button>
                              <Button
                                size="sm"
                                color="light"
                                onClick={() => openTemplateModal(tpl)}
                                title="Edit Global Template"
                              >
                                <RiEditLine />
                              </Button>
                              <Button
                                size="sm"
                                color="outline-danger"
                                onClick={() => handleDeleteTemplate(tpl.id)}
                                title="Delete Template"
                              >
                                <RiDeleteBinLine />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </TabPane>

        {/* ================= LOGS TAB ================= */}
        <TabPane tabId="logs">
          <Card className="border shadow-sm">
            <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h5 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-2">
                  <RiHistoryLine className="text-info" />
                  Platform Communication Dispatch Logs
                </h5>
                <small className="text-muted">
                  Audit trail tracking every Meta WhatsApp & Email dispatch, priority provider resolution, and execution status.
                </small>
              </div>
              <Button
                size="sm"
                color="outline-primary"
                onClick={fetchLogs}
                disabled={logsLoading}
              >
                <RiRefreshLine className={`me-1 ${logsLoading ? 'bx-spin' : ''}`} /> Refresh Logs
              </Button>
            </CardHeader>

            <CardBody className="p-3">
              {/* Filter Bar */}
              <Row className="g-2 mb-3">
                <Col md={3}>
                  <Select
                    value={{ value: logFilter.channel, label: `Channel: ${logFilter.channel}` }}
                    options={[
                      { value: 'ALL', label: 'All Channels' },
                      { value: 'EMAIL', label: 'Email' },
                      { value: 'WHATSAPP', label: 'WhatsApp' },
                    ]}
                    onChange={(opt) => setLogFilter((p) => ({ ...p, channel: opt.value }))}
                  />
                </Col>
                <Col md={3}>
                  <Select
                    value={{ value: logFilter.status, label: `Status: ${logFilter.status}` }}
                    options={[
                      { value: 'ALL', label: 'All Statuses' },
                      { value: 'SENT', label: 'Sent' },
                      { value: 'FAILED', label: 'Failed' },
                      { value: 'NOTIFICATION_DISABLED', label: 'Toggle Disabled' },
                      { value: 'CREDENTIAL_NOT_CONFIGURED', label: 'No Credentials' },
                      { value: 'SKIPPED', label: 'Skipped' },
                    ]}
                    onChange={(opt) => setLogFilter((p) => ({ ...p, status: opt.value }))}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    placeholder="Search by recipient, email, mobile, subject, or message..."
                    value={logFilter.search}
                    onChange={(e) => setLogFilter((p) => ({ ...p, search: e.target.value }))}
                  />
                </Col>
              </Row>

              {/* Table */}
              <div className="table-responsive">
                <Table hover className="align-middle mb-0 font-size-13">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '130px' }}>Timestamp</th>
                      <th>Clinic</th>
                      <th style={{ width: '100px' }}>Channel</th>
                      <th>Recipient</th>
                      <th>Event / Purpose</th>
                      <th style={{ width: '90px' }}>Source</th>
                      <th style={{ width: '120px' }}>Status</th>
                      <th style={{ width: '60px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsLoading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4 text-muted">
                          <Spinner size="sm" className="me-2" /> Loading dispatch logs...
                        </td>
                      </tr>
                    ) : logs.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4 text-muted">
                          No communication dispatch logs found.
                        </td>
                      </tr>
                    ) : (
                      logs.map((log) => (
                        <tr key={log.id}>
                          <td className="text-muted font-size-12">
                            {moment(log.created_at).format('DD MMM, hh:mm A')}
                          </td>
                          <td>
                            <strong className="text-dark">
                              {log.clinic?.clinic_name || `Clinic #${log.clinic_id || 'Global'}`}
                            </strong>
                          </td>
                          <td>
                            {log.channel === 'EMAIL' ? (
                              <Badge color="primary" className="font-size-11">
                                <RiMailLine className="me-1" /> Email
                              </Badge>
                            ) : (
                              <Badge color="success" className="font-size-11">
                                <RiWhatsappLine className="me-1" /> WhatsApp
                              </Badge>
                            )}
                          </td>
                          <td>
                            <div>
                              <span className="font-weight-semibold text-dark">{log.recipient_name || 'N/A'}</span>
                              <div className="text-muted font-size-11">{log.recipient_target}</div>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border font-size-11">
                              {log.event_name || log.notification_type}
                            </span>
                            {log.subject && <div className="text-muted font-size-11 text-truncate" style={{ maxWidth: 200 }}>{log.subject}</div>}
                          </td>
                          <td>{renderSourceBadge(log.credential_source)}</td>
                          <td>{renderStatusBadge(log.status)}</td>
                          <td>
                            <Button
                              size="sm"
                              color="light"
                              onClick={() => setSelectedLog(log)}
                              title="View Log Details"
                            >
                              <RiEyeLine />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </TabPane>

        {/* ================= PAYU SUBSCRIPTION GATEWAY TAB ================= */}
        <TabPane tabId="payu">
          <Row>
            <Col lg={8}>
              <Card className="border shadow-sm mb-4">
                <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <h6 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-1.5">
                      <RiBankCardLine className="text-danger font-size-18" />
                      PayU India Payment Gateway (SaaS Subscriptions & Renewals Only)
                    </h6>
                    <small className="text-muted">
                      Configures the official PayU gateway exclusively for clinic subscriptions, plan upgrades, and recurring auto-debits.
                    </small>
                  </div>
                  <Badge color={globalConfig.payu_is_active ? 'success' : 'secondary'} className="px-3 py-2">
                    {globalConfig.payu_is_active ? 'Gateway Active' : 'Gateway Inactive'}
                  </Badge>
                </CardHeader>
                <CardBody className="p-4">
                  <div className="alert alert-info font-size-12 mb-4 d-flex align-items-center gap-2">
                    <RiSecurePaymentLine className="font-size-20 flex-shrink-0" />
                    <div>
                      <strong>Strict Scope Policy:</strong> This gateway is strictly used for <strong>SaaS platform subscriptions & clinic renewals</strong>. Patient treatment billing and OPD invoices do not use PayU.
                    </div>
                  </div>

                  <Form onSubmit={handleSavePayu}>
                    <Row className="g-3">
                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Environment Mode *</Label>
                          <Input
                            type="select"
                            value={globalConfig.payu_env || 'test'}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, payu_env: e.target.value }))}
                          >
                            <option value="test">Sandbox / Test Mode (test.payu.in)</option>
                            <option value="production">Live Production (secure.payu.in)</option>
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Gateway Status</Label>
                          <div className="form-check form-switch form-switch-md mt-2">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              id="payuActiveSwitch"
                              checked={Boolean(globalConfig.payu_is_active)}
                              onChange={(e) => setGlobalConfig((p) => ({ ...p, payu_is_active: e.target.checked }))}
                            />
                            <Label className="form-check-label ms-2 font-size-12" htmlFor="payuActiveSwitch">
                              Enable PayU for SaaS Subscriptions
                            </Label>
                          </div>
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Merchant Key (Public Key) *</Label>
                          <Input
                            type="text"
                            placeholder="e.g. 7rnFly / J0ABC123"
                            value={globalConfig.payu_merchant_key || ''}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, payu_merchant_key: e.target.value }))}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Merchant Salt (Secret Key) *</Label>
                          <Input
                            type="password"
                            placeholder={globalConfig.has_payu_salt ? '•••••••••••••••• (Leave blank to keep unchanged)' : 'Enter PayU Merchant Salt'}
                            value={globalConfig.payu_merchant_salt || ''}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, payu_merchant_salt: e.target.value }))}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={12}>
                        <FormGroup>
                          <Label className="font-weight-semibold font-size-12">Server-to-Server Auth Header (Optional)</Label>
                          <Input
                            type="text"
                            placeholder="Optional OAuth or Auth token for Enterprise PayU APIs"
                            value={globalConfig.payu_auth_header || ''}
                            onChange={(e) => setGlobalConfig((p) => ({ ...p, payu_auth_header: e.target.value }))}
                          />
                          <small className="text-muted font-size-11">
                            Used for backend recurring SI mandate status verification and invoice lookups.
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                      <Button
                        type="button"
                        color="light"
                        onClick={fetchGlobalConfig}
                        disabled={savingPayu}
                      >
                        Reset Changes
                      </Button>
                      <Button
                        type="submit"
                        color="danger"
                        disabled={savingPayu}
                        className="px-4"
                      >
                        {savingPayu ? <Spinner size="sm" className="me-1" /> : <RiShieldCheckLine className="me-1" />}
                        Save PayU Gateway Config
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            {/* Gateway Endpoints & Webhook Info Card */}
            <Col lg={4}>
              <Card className="border shadow-sm bg-light">
                <CardHeader className="bg-white border-bottom py-3">
                  <h6 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-1.5">
                    <RiCodeLine className="text-primary font-size-16" />
                    PayU Integration Endpoints
                  </h6>
                  <small className="text-muted">Webhook and callback URLs configured in PayU dashboard</small>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="mb-3">
                    <label className="text-muted font-size-11 font-weight-bold">PAYMENT RETURN CALLBACK URL (surl / furl)</label>
                    <div className="p-2 bg-white border rounded font-size-11 text-break font-monospace text-dark">
                      {window.location.origin}/api/v1/subscriptions/payu-callback
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="text-muted font-size-11 font-weight-bold">SUPPORTED PAYMENT MODES</label>
                    <ul className="font-size-12 text-muted ps-3 mb-0">
                      <li>UPI & QR Auto-pay Mandates</li>
                      <li>Credit / Debit Cards with Standing Instructions (SI)</li>
                      <li>Net Banking & Corporate Accounts</li>
                      <li>Prepaid Cards & Wallets</li>
                    </ul>
                  </div>

                  <div className="p-2 bg-soft-success border border-success-subtle rounded font-size-12 text-success">
                    <RiCheckboxCircleFill className="me-1" />
                    SHA-512 dual hash verification is strictly enforced on all incoming webhook responses.
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </TabContent>

      {/* Template Create / Edit Modal */}
      <Modal isOpen={isTemplateModal} toggle={() => setIsTemplateModal(false)} size="lg">
        <ModalHeader toggle={() => setIsTemplateModal(false)}>
          {editingTemplate ? 'Edit Global Communication Template' : 'Create Global Communication Template'}
        </ModalHeader>
        <Form onSubmit={handleSaveTemplate}>
          <ModalBody className="p-4">
            <Row className="g-3 mb-2">
              <Col md={4}>
                <FormGroup>
                  <Label className="font-weight-semibold font-size-12">Channel Type *</Label>
                  <Select
                    value={{ value: templateForm.template_type, label: templateForm.template_type }}
                    options={[
                      { value: 'WHATSAPP', label: 'WhatsApp (Meta Cloud API)' },
                      { value: 'EMAIL', label: 'Email Template' },
                    ]}
                    onChange={(val) => setTemplateForm((p) => ({ ...p, template_type: val.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className="font-weight-semibold font-size-12">Template Key *</Label>
                  <Input
                    type="text"
                    placeholder="e.g. APPOINTMENT_CONFIRMATION"
                    value={templateForm.template_key}
                    onChange={(e) => setTemplateForm((p) => ({ ...p, template_key: e.target.value.toUpperCase() }))}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className="font-weight-semibold font-size-12">Category</Label>
                  <Select
                    value={{ value: templateForm.category, label: templateForm.category }}
                    options={[
                      { value: 'UTILITY', label: 'UTILITY' },
                      { value: 'MARKETING', label: 'MARKETING' },
                      { value: 'AUTHENTICATION', label: 'AUTHENTICATION' },
                    ]}
                    onChange={(val) => setTemplateForm((p) => ({ ...p, category: val.value }))}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="g-3 mb-2">
              <Col md={6}>
                <FormGroup>
                  <Label className="font-weight-semibold font-size-12">Template Display Title *</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Appointment Confirmation Notice"
                    value={templateForm.title}
                    onChange={(e) => setTemplateForm((p) => ({ ...p, title: e.target.value }))}
                    required
                  />
                </FormGroup>
              </Col>
              {templateForm.template_type === 'WHATSAPP' ? (
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-weight-semibold font-size-12">Meta Template Name (Graph API)</Label>
                    <Input
                      type="text"
                      placeholder="e.g. appointment_confirmation"
                      value={templateForm.meta_template_name}
                      onChange={(e) => setTemplateForm((p) => ({ ...p, meta_template_name: e.target.value.toLowerCase() }))}
                    />
                    <small className="text-muted font-size-11">Name approved in Meta WhatsApp Manager</small>
                  </FormGroup>
                </Col>
              ) : (
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-weight-semibold font-size-12">Email Subject Line *</Label>
                    <Input
                      type="text"
                      placeholder="e.g. ✅ Dental Appointment Confirmed on {{appointment_date}}"
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm((p) => ({ ...p, subject: e.target.value }))}
                      required
                    />
                  </FormGroup>
                </Col>
              )}
            </Row>

            {/* Variable Insertion Badges */}
            <div className="mb-2 p-2 bg-light rounded border">
              <span className="font-size-11 font-weight-bold text-muted me-2">INSERT VARIABLES:</span>
              {[
                '{{patient_name}}',
                '{{doctor_name}}',
                '{{appointment_date}}',
                '{{clinic_name}}',
                '{{chair_code}}',
                '{{number}}',
                '{{amount}}',
              ].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="btn btn-xs btn-outline-primary me-1 mb-1 font-size-11 py-0.5 px-1.5"
                  onClick={() => insertTag(tag)}
                >
                  + {tag}
                </button>
              ))}
            </div>

            <FormGroup className="mb-3">
              <Label className="font-weight-semibold font-size-12">Body Text Content *</Label>
              <Input
                type="textarea"
                rows={5}
                value={templateForm.body_text}
                onChange={(e) => setTemplateForm((p) => ({ ...p, body_text: e.target.value }))}
                required
              />
            </FormGroup>

            {templateForm.template_type === 'WHATSAPP' && (
              <Row className="g-3">
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-weight-semibold font-size-12">Header Text (Optional)</Label>
                    <Input
                      type="text"
                      placeholder="Appointment Confirmed 🦷"
                      value={templateForm.header_text}
                      onChange={(e) => setTemplateForm((p) => ({ ...p, header_text: e.target.value }))}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-weight-semibold font-size-12">Footer Text (Optional)</Label>
                    <Input
                      type="text"
                      placeholder="Oralstop Dental Healthcare"
                      value={templateForm.footer_text}
                      onChange={(e) => setTemplateForm((p) => ({ ...p, footer_text: e.target.value }))}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={() => setIsTemplateModal(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={savingTemplate}>
              {savingTemplate ? <Spinner size="sm" className="me-1" /> : <RiShieldCheckLine className="me-1" />}
              Save Template
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Live Preview Modal */}
      <Modal isOpen={Boolean(previewTemplate)} toggle={() => setPreviewTemplate(null)} size="md" centered>
        <ModalHeader toggle={() => setPreviewTemplate(null)}>
          Live Render Preview: {previewTemplate?.title}
        </ModalHeader>
        <ModalBody className="p-4">
          {previewTemplate && (
            <div>
              <div className="mb-3">
                <span className="badge bg-soft-primary text-primary me-2">{previewTemplate.template_type}</span>
                <code className="text-dark font-weight-bold">{previewTemplate.template_key}</code>
              </div>

              {/* WhatsApp Mock Bubble */}
              {previewTemplate.template_type === 'WHATSAPP' ? (
                <div
                  className="p-3 rounded shadow-sm"
                  style={{
                    backgroundColor: '#e7fed9',
                    border: '1px solid #c3e6cb',
                    maxWidth: '90%',
                    fontFamily: 'Segoe UI, Helvetica, Arial, sans-serif',
                  }}
                >
                  {previewTemplate.header_text && (
                    <div className="font-weight-bold text-dark font-size-14 mb-1">
                      {previewTemplate.header_text}
                    </div>
                  )}
                  <div className="text-dark font-size-13" style={{ whiteSpace: 'pre-wrap' }}>
                    {getRenderedPreview(previewTemplate)}
                  </div>
                  {previewTemplate.footer_text && (
                    <div className="text-muted font-size-11 mt-2 pt-1 border-top border-success-subtle">
                      {previewTemplate.footer_text}
                    </div>
                  )}
                  <div className="text-end text-muted font-size-10 mt-1">10:30 AM ✓✓</div>
                </div>
              ) : (
                <div className="p-3 bg-white border rounded shadow-sm">
                  <div className="border-bottom pb-2 mb-2 font-size-13">
                    <strong>Subject:</strong> {previewTemplate.subject}
                  </div>
                  <div className="font-size-13" style={{ whiteSpace: 'pre-wrap' }}>
                    {getRenderedPreview(previewTemplate)}
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPreviewTemplate(null)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Log Details Modal */}
      <Modal isOpen={Boolean(selectedLog)} toggle={() => setSelectedLog(null)} size="lg">
        <ModalHeader toggle={() => setSelectedLog(null)}>
          Communication Dispatch Audit Details #{selectedLog?.id}
        </ModalHeader>
        <ModalBody>
          {selectedLog && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong className="text-muted d-block font-size-11">CHANNEL & STATUS</strong>
                  <div className="d-flex align-items-center gap-2 mt-1">
                    <span className="font-weight-bold">{selectedLog.channel}</span>
                    {renderStatusBadge(selectedLog.status)}
                    {renderSourceBadge(selectedLog.credential_source)}
                  </div>
                </Col>
                <Col md={6}>
                  <strong className="text-muted d-block font-size-11">DISPATCH TIMESTAMP</strong>
                  <div className="font-weight-semibold text-dark mt-1">
                    {moment(selectedLog.created_at).format('DD MMMM YYYY, hh:mm:ss A')}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong className="text-muted d-block font-size-11">RECIPIENT</strong>
                  <div className="font-weight-bold text-dark">{selectedLog.recipient_name} ({selectedLog.recipient_type})</div>
                  <div className="text-muted font-size-12">{selectedLog.recipient_target}</div>
                </Col>
                <Col md={6}>
                  <strong className="text-muted d-block font-size-11">EVENT / PURPOSE</strong>
                  <div className="badge bg-light text-dark border font-size-12 mt-1">{selectedLog.event_name}</div>
                  {selectedLog.subject && <div className="text-dark font-size-12 mt-1"><strong>Subject:</strong> {selectedLog.subject}</div>}
                </Col>
              </Row>

              {selectedLog.status_reason && (
                <div className="alert alert-warning font-size-12 py-2 px-3 mb-3">
                  <strong>Reason / Notice:</strong> {selectedLog.status_reason}
                </div>
              )}

              <div className="mb-3">
                <strong className="text-muted d-block font-size-11 mb-1">MESSAGE PREVIEW</strong>
                <div className="p-3 bg-light border rounded font-size-12" style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto' }}>
                  {selectedLog.message_preview || 'No message content available.'}
                </div>
              </div>

              {selectedLog.response_payload && (
                <div>
                  <strong className="text-muted d-block font-size-11 mb-1">GATEWAY RESPONSE PAYLOAD</strong>
                  <pre className="p-2 bg-dark text-light border rounded font-size-11 mb-0" style={{ maxHeight: 150, overflowY: 'auto' }}>
                    {selectedLog.response_payload}
                  </pre>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSelectedLog(null)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default GlobalCommunicationManager;
